import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useLang } from "@/lib/i18n";
import {
  MapPin, Code2, Bot, FileText, Tags, ArrowRight, Radar,
  ShieldCheck, Zap, ClipboardCheck, Link2,
} from "lucide-react";

const TOOL_META = [
  { path: "/site-audit", icon: Radar, time: "~60 sec", featured: true, account: true },
  { path: "/local-rank", icon: MapPin, time: "~15 sec", account: true },
  { path: "/indexnow", icon: Zap, time: "~30 sec", account: true },
  { path: "/link-tools", icon: Link2, time: "~30 sec" },
  { path: "/meta-generator", icon: Tags, time: "~2 min" },
  { path: "/schema-generator", icon: Code2, time: "~3 min" },
  { path: "/robots-checker", icon: Bot, time: "~5 sec" },
  { path: "/llms-txt", icon: FileText, time: "~4 min" },
];

const T = {
  en: {
    badge: "rang1.ca - be ready for the AI search revolution",
    heroTitle: "Rank first for AI-powered search",
    heroBody:
      "Search is moving to ChatGPT, Perplexity and Google AI Overviews - and they only cite websites they can read and understand. Rang1 audits your site, checks your local ranking, and gives you the exact files and tags AI engines look for.",
    heroPrimary: "Audit my site's AI readiness",
    heroSecondary: "Check AI bot access",
    toolsTitle: "The toolkit",
    toolsSub: "Six focused tools. Deterministic results. Free account for audits and rank checks.",
    open: "Open tool",
    startHere: "Start here",
    freeAccount: "Free account",
    noLogin: "No login",
    tools: {
      "/site-audit": {
        title: "Sitewide SEO Audit",
        desc: "Scan up to 20 pages at once. Overall AI-readiness score, prioritized fixes and a downloadable structured report.",
        bullets: ["Sitemap-based crawl", "Improvements ranked by impact", "Download report"],
      },
      "/local-rank": {
        title: "Local Rank Checker",
        desc: "See where your business ranks on Google Maps for the keywords your customers actually search.",
        bullets: ["Live Google Maps top 40", "Competitors above you", "Google Business Profile status"],
      },
      "/indexnow": {
        title: "IndexNow Submitter",
        desc: "Instantly push your new or updated pages to Bing, Yandex, Naver and Seznam - the fastest path into ChatGPT and Copilot answers.",
        bullets: ["One ping, 5 engines", "Feeds ChatGPT via Bing", "Key setup in 30 seconds"],
      },
      "/link-tools": {
        title: "Link Tools",
        desc: "White-hat backlink work: find broken links to reclaim, audit any page's links, and surface internal-linking opportunities on your own site.",
        bullets: ["Broken link finder", "Internal link optimizer", "Link profile auditor"],
      },
      "/meta-generator": {
        title: "Meta Tags Generator",
        desc: "Build perfect title, description, Open Graph and Twitter tags with live previews.",
        bullets: ["Google SERP preview", "Social card previews", "Copy HTML"],
      },
      "/schema-generator": {
        title: "Schema Generator",
        desc: "Generate valid JSON-LD for 9 schema types to unlock rich results and AI citations.",
        bullets: ["Article, FAQ, Product +6", "Detect existing schema", "Copy / download"],
      },
      "/robots-checker": {
        title: "Robots.txt AI Checker",
        desc: "See exactly which AI crawlers (GPTBot, ClaudeBot, PerplexityBot...) can access your site.",
        bullets: ["20 bots checked", "Matched rule shown", "Generate robots.txt"],
      },
      "/llms-txt": {
        title: "llms.txt Generator",
        desc: "Create an llms.txt file - the emerging standard that tells AI assistants what matters on your site.",
        bullets: ["Guided form", "Valid markdown", "Download llms.txt"],
      },
    },
    promisesTitle: "Why Rang1 works differently",
    promises: [
      {
        icon: ShieldCheck,
        title: "Rule-based, not AI guesses",
        desc: "Every score and recommendation comes from deterministic checks against published standards - the same result every time.",
      },
      {
        icon: Zap,
        title: "Free to use",
        desc: "Generators and checkers are open to everyone. A free account unlocks the sitewide audit and local rank checker, and saves your reports.",
      },
      {
        icon: ClipboardCheck,
        title: "Copy-ready outputs",
        desc: "Everything you generate is valid, formatted and ready to paste into your site: HTML, JSON-LD, robots.txt, llms.txt.",
      },
    ],
  },
  fr: {
    badge: "rang1.ca - prêt pour la révolution de la recherche IA",
    heroTitle: "Soyez au premier rang pour la recherche avec IA",
    heroBody:
      "La recherche migre vers ChatGPT, Perplexity et les aperçus IA de Google - et ces moteurs ne citent que les sites qu'ils peuvent lire et comprendre. Rang1 audite votre site, vérifie votre positionnement local et vous donne exactement les fichiers et balises que les moteurs IA recherchent.",
    heroPrimary: "Auditer la préparation IA de mon site",
    heroSecondary: "Vérifier l'accès des robots IA",
    toolsTitle: "La boîte à outils",
    toolsSub: "Six outils ciblés. Résultats déterministes. Compte gratuit pour les audits et vérifications de rang.",
    open: "Ouvrir l'outil",
    startHere: "Commencez ici",
    freeAccount: "Compte gratuit",
    noLogin: "Sans connexion",
    tools: {
      "/site-audit": {
        title: "Audit SEO complet du site",
        desc: "Analysez jusqu'à 20 pages d'un coup. Score de préparation IA, correctifs priorisés et rapport structuré téléchargeable.",
        bullets: ["Exploration via sitemap", "Améliorations classées par impact", "Rapport téléchargeable"],
      },
      "/local-rank": {
        title: "Vérificateur de rang local",
        desc: "Voyez où votre entreprise se classe sur Google Maps pour les mots-clés que vos clients recherchent vraiment.",
        bullets: ["Top 40 Google Maps en direct", "Les concurrents devant vous", "Statut du profil Google Business"],
      },
      "/indexnow": {
        title: "Soumission IndexNow",
        desc: "Poussez instantanément vos pages nouvelles ou modifiées vers Bing, Yandex, Naver et Seznam - la voie la plus rapide vers les réponses de ChatGPT et Copilot.",
        bullets: ["Un ping, 5 moteurs", "Alimente ChatGPT via Bing", "Configuration en 30 secondes"],
      },
      "/link-tools": {
        title: "Outils de liens",
        desc: "Backlinks white-hat : trouvez les liens cassés à récupérer, auditez les liens d'une page, et révélez les opportunités de maillage interne sur votre site.",
        bullets: ["Détecteur de liens cassés", "Optimiseur de liens internes", "Auditeur de profil de liens"],
      },
      "/meta-generator": {
        title: "Générateur de balises méta",
        desc: "Créez des balises title, description, Open Graph et Twitter parfaites avec aperçus en direct.",
        bullets: ["Aperçu Google", "Aperçus réseaux sociaux", "HTML à copier"],
      },
      "/schema-generator": {
        title: "Générateur de schéma",
        desc: "Générez du JSON-LD valide pour 9 types de schémas : résultats enrichis et citations par les IA.",
        bullets: ["Article, FAQ, Produit +6", "Détection du schéma existant", "Copier / télécharger"],
      },
      "/robots-checker": {
        title: "Vérificateur robots.txt IA",
        desc: "Voyez exactement quels robots IA (GPTBot, ClaudeBot, PerplexityBot...) peuvent accéder à votre site.",
        bullets: ["20 robots vérifiés", "Règle appliquée affichée", "Générer un robots.txt"],
      },
      "/llms-txt": {
        title: "Générateur llms.txt",
        desc: "Créez un fichier llms.txt - le standard émergent qui indique aux assistants IA ce qui compte sur votre site.",
        bullets: ["Formulaire guidé", "Markdown valide", "Télécharger llms.txt"],
      },
    },
    promisesTitle: "Pourquoi Rang1 est différent",
    promises: [
      {
        icon: ShieldCheck,
        title: "Des règles, pas des suppositions d'IA",
        desc: "Chaque score et recommandation vient de vérifications déterministes basées sur des standards publiés - le même résultat à chaque fois.",
      },
      {
        icon: Zap,
        title: "Gratuit",
        desc: "Les générateurs et vérificateurs sont ouverts à tous. Un compte gratuit débloque l'audit complet et le rang local, et conserve vos rapports.",
      },
      {
        icon: ClipboardCheck,
        title: "Résultats prêts à coller",
        desc: "Tout ce que vous générez est valide, formaté et prêt à intégrer : HTML, JSON-LD, robots.txt, llms.txt.",
      },
    ],
  },
};

export default function Dashboard() {
  const { lang } = useLang();
  const t = T[lang];

  return (
    <div>
      {/* Hero */}
      <section className="hero-gradient noise border-b">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-16 sm:py-24">
          <div className="max-w-3xl">
            <Badge variant="outline" className="mb-5 bg-card text-xs font-medium" data-testid="dashboard-hero-badge">
              {t.badge}
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
              {t.heroTitle}
            </h1>
            <p className="mt-5 text-base md:text-lg text-muted-foreground max-w-2xl">
              {t.heroBody}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" data-testid="dashboard-hero-primary-cta">
                <Link to="/site-audit">
                  {t.heroPrimary} <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="bg-card" data-testid="dashboard-hero-secondary-cta">
                <Link to="/robots-checker">{t.heroSecondary}</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Tools grid */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-12 sm:py-16">
        <h2 className="text-xl sm:text-2xl font-semibold tracking-tight mb-2">{t.toolsTitle}</h2>
        <p className="text-sm text-muted-foreground mb-8">{t.toolsSub}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="dashboard-tools-grid">
          {TOOL_META.map((tool, i) => {
            const tt = t.tools[tool.path];
            return (
              <motion.div
                key={tool.path}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: i * 0.07 }}
              >
                <Card className={`tool-card h-full border ${tool.featured ? "border-primary/40 ring-1 ring-primary/20" : ""}`} data-testid={`tool-card-${tool.path.slice(1)}`}>
                  <CardContent className="p-5 sm:p-6 flex flex-col h-full">
                    <div className="flex items-start justify-between">
                      <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent text-accent-foreground">
                        <tool.icon className="h-5 w-5" />
                      </span>
                      <div className="flex items-center gap-1.5">
                        {tool.featured && <Badge className="text-[10px]">{t.startHere}</Badge>}
                        <Badge variant="secondary" className="text-xs">{tool.time}</Badge>
                      </div>
                    </div>
                    <h3 className="mt-4 font-heading font-semibold text-base">{tt.title}</h3>
                    <p className="mt-1.5 text-sm text-muted-foreground flex-1">{tt.desc}</p>
                    <ul className="mt-4 space-y-1">
                      {tt.bullets.map((b) => (
                        <li key={b} className="text-xs text-muted-foreground flex items-center gap-1.5">
                          <span className="h-1 w-1 rounded-full bg-primary shrink-0" />
                          {b}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-5 flex items-center justify-between">
                      <Button asChild variant={tool.featured ? "default" : "outline"} size="sm" data-testid={`tool-card-open-${tool.path.slice(1)}`}>
                        <Link to={tool.path}>
                          {t.open} <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                        </Link>
                      </Button>
                      <span className="text-[11px] text-muted-foreground">{tool.account ? t.freeAccount : t.noLogin}</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Deterministic promise */}
      <section className="border-t bg-card">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-12 sm:py-16">
          <h2 className="text-xl sm:text-2xl font-semibold tracking-tight mb-8">{t.promisesTitle}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {t.promises.map((p) => (
              <div key={p.title} className="flex gap-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-foreground">
                  <p.icon className="h-5 w-5" />
                </span>
                <div>
                  <h3 className="font-medium text-sm">{p.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
